import axios, { CreateAxiosDefaults, InternalAxiosRequestConfig } from 'axios';
import { authService, SessionExpiredError } from './AuthService';

interface AuthenticatedRequest extends InternalAxiosRequestConfig {
  authRetried?: boolean;
}

export function createAuthenticatedClient(settings: CreateAxiosDefaults = {}) {
  const client = axios.create(settings);
  client.interceptors.request.use(async request => {
    const user = await authService.getValidUser();
    request.headers.Authorization = `Bearer ${user.access_token}`;
    return request;
  });
  client.interceptors.response.use(response => response, async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401 || !error.config) throw error;
    const request = error.config as AuthenticatedRequest;
    if (request.signal?.aborted) throw error;
    const body = error.response.data instanceof Blob ? await error.response.data.text() : error.response.data;
    // Aðeins endurtaka beiðni sem auðkenningarlagið hafnaði áður en aðgerðin hófst.
    if (body !== 'Token has expired' && body !== 'Invalid token' &&
      !String(error.response.headers['www-authenticate'] || '').includes('invalid_token')) throw error;
    const sentToken = String(request.headers.Authorization || '').replace(/^Bearer /, '');
    if (request.authRetried) {
      await authService.rejectAccessToken(sentToken);
      if (authService.isSessionExpired()) throw new SessionExpiredError();
      throw error;
    }
    request.authRetried = true;
    const current = await authService.getUser();
    if (!current || current.expired || current.access_token === sentToken) await authService.renewToken();
    return client.request(request);
  });
  return client;
}

export const apiClient = createAuthenticatedClient();
